/**
 * Curated theme templates for the consumer onboarding wizard.
 *
 * Each template is a designer-harmonized combination of:
 *   - primaryColor   : the booth's accent / CTA color
 *   - secondaryColor : text/surface color that reads on the primary
 *   - backgroundColor : the booth's actual background (SOLID — see note)
 *   - gradient       : a CSS gradient used ONLY for onboarding decoration
 *                       (theme cards, wizard chrome, live-preview accent)
 *
 * NOTE ON BACKGROUNDS
 * -------------------
 * The booth background is a solid color. The renderer's BackgroundContainer
 * applies `theme.backgroundColor` as a literal CSS color, and a calm solid
 * reads better behind photos than a busy backdrop. The `gradient` field is
 * decorative chrome for the onboarding flow, not the booth itself. (Real
 * gradient/image booth backgrounds are a possible later enhancement that
 * would touch the theme model + renderer.)
 *
 * Colors lean on the studio brand palette (tailwind.config.js) so the booth
 * feels of-a-piece with dreamboothstudio.com.
 */

export interface ThemeTemplate {
  id: string
  name: string
  /** One-line mood descriptor shown under the name. */
  mood: string
  primaryColor: string
  secondaryColor: string
  backgroundColor: string
  /** CSS gradient string for onboarding decoration only. */
  gradient: string
  /** True when the palette is dark (affects preview text contrast). */
  dark?: boolean
}

export const THEME_TEMPLATES: ThemeTemplate[] = [
  {
    id: 'studio-blue',
    name: 'Studio Blue',
    mood: 'Clean & modern',
    primaryColor: '#007AFF',
    secondaryColor: '#FBFBFB',
    backgroundColor: '#FBFBFB',
    gradient: 'linear-gradient(135deg, #007AFF 0%, #62CDBD 100%)'
  },
  {
    id: 'sunset-pop',
    name: 'Sunset Pop',
    mood: 'Playful & vibrant',
    primaryColor: '#FE4205',
    secondaryColor: '#FFFFFF',
    backgroundColor: '#FFF6F0',
    gradient: 'linear-gradient(135deg, #63ACA8 0%, #CC6CE6 40%, #F28BB1 70%, #FFDE5A 100%)'
  },
  {
    id: 'mono-noir',
    name: 'Mono Noir',
    mood: 'Sleek & editorial',
    primaryColor: '#FBFBFB',
    secondaryColor: '#1D1D1D',
    backgroundColor: '#1D1D1D',
    gradient: 'linear-gradient(135deg, #1D1D1D 0%, #424242 100%)',
    dark: true
  },
  {
    id: 'candy',
    name: 'Candy',
    mood: 'Sweet & fun',
    primaryColor: '#F28BB1',
    secondaryColor: '#FFFFFF',
    backgroundColor: '#FFF0F6',
    gradient: 'linear-gradient(135deg, #F28BB1 0%, #CC6CE6 100%)'
  },
  {
    id: 'teal-calm',
    name: 'Teal Calm',
    mood: 'Fresh & serene',
    primaryColor: '#63ACA8',
    secondaryColor: '#FFFFFF',
    backgroundColor: '#F0F8F7',
    gradient: 'linear-gradient(135deg, #62CDBD 0%, #0998BA 100%)'
  },
  {
    id: 'golden-hour',
    name: 'Golden Hour',
    mood: 'Warm & inviting',
    primaryColor: '#E4A836',
    secondaryColor: '#3A2A12',
    backgroundColor: '#FFF8EC',
    gradient: 'linear-gradient(135deg, #FFDE5A 0%, #E4A836 60%, #FE4205 100%)'
  },
  {
    id: 'grape',
    name: 'Grape',
    mood: 'Bold & creative',
    primaryColor: '#CC6CE6',
    secondaryColor: '#FFFFFF',
    backgroundColor: '#F7F0FB',
    gradient: 'linear-gradient(135deg, #CC6CE6 0%, #0998BA 100%)'
  },
  {
    id: 'coral',
    name: 'Coral',
    mood: 'Friendly & lively',
    primaryColor: '#F28BB1',
    secondaryColor: '#FFFFFF',
    backgroundColor: '#FFF3F1',
    gradient: 'linear-gradient(135deg, #FE4205 0%, #F28BB1 100%)'
  },
  {
    id: 'forest',
    name: 'Forest',
    mood: 'Earthy & grounded',
    primaryColor: '#025E4A',
    secondaryColor: '#FFFFFF',
    backgroundColor: '#EEF5F0',
    gradient: 'linear-gradient(135deg, #025E4A 0%, #63ACA8 100%)'
  },
  {
    id: 'midnight',
    name: 'Midnight',
    mood: 'Premium & nocturnal',
    primaryColor: '#62CDBD',
    secondaryColor: '#FBFBFB',
    backgroundColor: '#161A2B',
    gradient: 'linear-gradient(135deg, #0998BA 0%, #CC6CE6 100%)',
    dark: true
  }
]

export const DEFAULT_THEME_TEMPLATE_ID = 'studio-blue'

export function getThemeTemplate(id: string): ThemeTemplate {
  return (
    THEME_TEMPLATES.find((t) => t.id === id) ??
    THEME_TEMPLATES.find((t) => t.id === DEFAULT_THEME_TEMPLATE_ID) ??
    THEME_TEMPLATES[0]
  )
}
